import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { isPendingCancellation } from "../utils/subscriptionDisplayStatus";

const AdminContractViewer = ({ customerId, vehicleId, onClose }) => {
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (customerId && vehicleId) {
      fetchContractData();
    }
  }, [customerId, vehicleId]);

  const fetchContractData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api(`v1/contract/admin/${customerId}/${vehicleId}`, "GET");

      if (response.data.success) {
        setContractData(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch contract data");
      }
    } catch (err) {
      console.error("Error fetching contract data:", err);
      setError("Failed to fetch contract data");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadContract = async (contractId) => {
    try {
      setDownloading(contractId);
      setError(null);

      const response = await api(`v1/contract/admin/download/${contractId}`, "GET");

      if (response.data.success) {
        // Open the download URL in a new tab
        window.open(response.data.data.downloadUrl, "_blank");

        // Optional: Show success message
        console.log("Contract download initiated successfully");
      } else {
        setError(response.data.message || "Failed to generate download link");
      }
    } catch (err) {
      console.error("Error downloading contract:", err);
      setError("Failed to download contract");
    } finally {
      setDownloading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", label: "Pending" },
      accepted: { color: "bg-green-100 text-green-800", label: "Signed" },
      rejected: { color: "bg-red-100 text-red-800", label: "Rejected" },
      expired: { color: "bg-gray-100 text-gray-800", label: "Expired" }
    };

    const config = statusConfig[status] || statusConfig["pending"];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10073E]"></div>
            <span className="ml-3 text-lg">Loading contract information...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#10073E] to-[#2D1B69] text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Contract Information</h2>
              <p className="text-gray-200 mt-1">Customer: {contractData?.latestContract?.customerName || "Unknown"}</p>
              <p className="text-gray-200">Vehicle: {contractData?.latestContract?.vehicleDetails || "Unknown"}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="text-red-400 mr-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-red-800">Error</h4>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                  <button
                    onClick={fetchContractData}
                    className="text-sm text-red-700 underline mt-1 hover:text-red-800"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {contractData && (
            <>
              {/* Subscription Overview */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Subscription Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Product Name</p>
                    <p className="font-medium">{contractData.subscription?.productName || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-medium">
                      {isPendingCancellation(contractData.subscription)
                        ? "Pending Cancellation"
                        : contractData.subscription?.active
                        ? "Active"
                        : "Inactive"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">
                      {contractData.subscription?.isMonthlyProduct ? "Monthly Price" : "Price Paid"}
                    </p>
                    <p className="font-medium text-green-600">
                      ${contractData.subscription?.pricePaid ? contractData.subscription.pricePaid.toFixed(2) : "0.00"}
                      {contractData.subscription?.isMonthlyProduct && (
                        <span className="text-xs text-gray-500 ml-1">/month</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">
                      {contractData.subscription?.isMonthlyProduct &&
                      contractData.subscription?.totalSubscriptionValue > 0
                        ? "Total Value"
                        : "RRP"}
                    </p>
                    <p className="font-medium text-blue-600">
                      $
                      {contractData.subscription?.isMonthlyProduct &&
                      contractData.subscription?.totalSubscriptionValue > 0
                        ? contractData.subscription.totalSubscriptionValue.toFixed(2)
                        : contractData.subscription?.rrp
                        ? contractData.subscription.rrp.toFixed(2)
                        : "0.00"}
                      {contractData.subscription?.isMonthlyProduct &&
                        contractData.subscription?.totalSubscriptionValue > 0 && (
                          <span className="text-xs text-gray-500 ml-1">total</span>
                        )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time Remaining</p>
                    <p className="font-medium text-purple-600">
                      {contractData.subscription?.timeRemainingFormatted || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Purchase Date</p>
                    <p className="font-medium">
                      {contractData.subscription?.purchaseDate
                        ? new Date(contractData.subscription.purchaseDate).toLocaleDateString("en-AU")
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contract Status Summary */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Contract Status</h3>
                  {getStatusBadge(contractData.isSigned ? "accepted" : "pending")}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="text-blue-400 mr-3">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-blue-900">
                        {contractData.isSigned ? "Contract Signed" : "No Signed Contract"}
                      </h4>
                      <p className="text-sm text-blue-700 mt-1">
                        {contractData.isSigned
                          ? `Contract was signed on ${new Date(
                              contractData.latestContract.signedDate
                            ).toLocaleDateString("en-AU")}`
                          : "This subscription requires a signed contract but none has been completed yet."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contract History */}
              {contractData.contracts && contractData.contracts.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contract History</h3>
                  <div className="space-y-4">
                    {contractData.contracts.map((contract, index) => (
                      <div key={contract.contractId} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">Contract #{index + 1}</span>
                            {getStatusBadge(contract.status)}
                          </div>
                          {contract.status === "accepted" && contract.contractPdfUrl && (
                            <button
                              onClick={() => handleDownloadContract(contract.contractId)}
                              disabled={downloading === contract.contractId}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#10073E] hover:bg-[#2D1B69] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#10073E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {downloading === contract.contractId ? (
                                <>
                                  <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                  </svg>
                                  Download PDF
                                </>
                              )}
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Created</p>
                            <p className="font-medium">{new Date(contract.createdAt).toLocaleDateString("en-AU")}</p>
                          </div>
                          {contract.signedDate && (
                            <div>
                              <p className="text-gray-600">Signed Date</p>
                              <p className="font-medium">{new Date(contract.signedDate).toLocaleDateString("en-AU")}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-gray-600">Has Digital Signature</p>
                            <p className="font-medium">{contract.hasSignature ? "Yes" : "No"}</p>
                          </div>
                          {contract.ipAddress && (
                            <div>
                              <p className="text-gray-600">IP Address</p>
                              <p className="font-medium">{contract.ipAddress}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Contracts */}
              {(!contractData.contracts || contractData.contracts.length === 0) && (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Contracts Found</h3>
                  <p className="text-gray-600">No contract records found for this customer and vehicle combination.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#10073E] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContractViewer;
